<script setup lang="ts">
/* "Most of X" on the dashboard: a short ranked list where each row's bar is
   its share of the largest. Bars rather than a chart because the question is
   only ever "which, and roughly how much more" -- the figure is printed
   beside the bar, so the bar never has to be read precisely. */
import { RouterLink } from 'vue-router'

export interface RankedItem {
  label: string
  value: number
  /** Printed beside the bar, already formatted. Defaults to the value. */
  display?: string
  /** Small line under the label. */
  note?: string
  to?: string
}

const props = defineProps<{ items: RankedItem[]; empty?: string }>()

const max = () => Math.max(1, ...props.items.map((i) => i.value))
</script>

<template>
  <p v-if="items.length === 0" class="empty">{{ empty ?? 'Nothing yet.' }}</p>
  <ol v-else class="list">
    <li v-for="item in items" :key="item.label" class="row">
      <div class="top">
        <component :is="item.to ? RouterLink : 'span'" :to="item.to" class="label" :class="{ link: item.to }">
          {{ item.label }}
        </component>
        <span class="value">{{ item.display ?? item.value }}</span>
      </div>
      <div class="track" aria-hidden="true">
        <span class="bar" :style="{ width: `${(item.value / max()) * 100}%` }" />
      </div>
      <p v-if="item.note" class="note">{{ item.note }}</p>
    </li>
  </ol>
</template>

<style scoped>
.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.top {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2xs);
  font-size: var(--text-sm);
}

.label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-heading);
}

.link {
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.value {
  flex: none;
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--color-heading);
}

.track {
  height: 0.5rem;
  margin-top: var(--space-3xs);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.bar {
  display: block;
  height: 100%;
  min-width: 0.5rem;
  background-color: var(--color-accent);
  border-radius: var(--radius-full);
}

.note {
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.empty {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
